/**
 * Export a contestant profile to PDF using jsPDF.
 *
 * Color migration (PR (m.4)) — 2026-01-26
 * --------------------------------------
 * Colors migrated from the old `gold-*` palette to the corrected
 * Meridian/Reignara palette. The 5 RGB triples now derive from the
 * tokens declared in `frontend/src/styles/tokens.css`:
 *
 *   G950 (was #2e241b — gold-950)         → INK    (#1E1A17 — --foreground)
 *   G600 (was #9c7c54 — gold-600)         → TAN    (#C9A87A — --primary)
 *   G500 (was #af9164 — gold-500 mid)     → MUTED  (#646973 — --muted-foreground)
 *   G400 (was #c4ae8a — gold-400 light)   → BORDER (#1E1A17 @ 60% — hairline tint)
 *   G300 (was #d9cbb1 — gold-300 lightest) → TINT   (#C9A87A @ 30% — light tan tint)
 *
 * Font migration (Bavex + Inter swap) — DEFERRED
 * ----------------------------------------------
 * Production design system uses Bavex (display) + Inter (sans). For
 * PDFs, however, Cormorant Garamond + Montserrat remain the better
 * choice for printed-body readability:
 *
 *   - Bavex is a display face — designed for italic page titles at
 *     26px+. At 8.5pt PDF body it would render thin and difficult to
 *     read on lower-quality color printers.
 *   - Inter is sans-serif which doesn't change PDF body readability
 *     materially vs Montserrat.
 *
 * Trade: PDF body fonts deviate from web body for legibility, but
 * remain visually anchored to the brand via Cormorant Garamond's
 * editorial-magazine character (similar in feel to Bavex's italic
 * displays). Future spec work may revisit if printed PDFs are
 * de-prioritized in favor of digital-only viewing.
 *
 * Spec: meridian-redesign — task (m.4)
 * Requirements: 18.5 (mockup parity)
 */

import { jsPDF } from 'jspdf';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { cormorantRegular } from './fonts/cormorantRegular';
import { cormorantBold } from './fonts/cormorantBold';
import { cormorantItalic } from './fonts/cormorantItalic';
import { montserratRegular } from './fonts/montserratRegular';
import { montserratBold } from './fonts/montserratBold';

// PR (m.4) — corrected token palette mapped to RGB triples for jsPDF.
// Aliases preserve the original variable names so the per-call sites
// don't need to change. The triples come from `tokens.css`:
//   #1E1A17 → [30, 26, 23]   (warm ink)
//   #C9A87A → [201, 168, 122] (camel-tan)
//   #646973 → [100, 105, 115] (muted gray)
//   #1E1A17 @ 60% on white → [157, 154, 152] (hairline tint)
//   #C9A87A @ 30% on white → [233, 220, 198] (light tan tint)
const G950: [number, number, number] = [30, 26, 23];     // INK
const G600: [number, number, number] = [201, 168, 122];  // TAN (brand)
const G500: [number, number, number] = [100, 105, 115];  // MUTED
const G400: [number, number, number] = [157, 154, 152];  // BORDER
const G300: [number, number, number] = [233, 220, 198];  // TINT

/** Apply rounded corners to an image data URL using canvas clipping */
function applyRoundedCorners(dataUrl: string, _radius: number = 20): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      // Use 12% of image size as corner radius for visible rounding
      const radius = Math.round(size * 0.12);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(dataUrl); return; }
      // Clear to transparent then fill white background with rounded corners
      ctx.clearRect(0, 0, size, size);
      // Fill white background first (PDF doesn't handle transparency well)
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.fill();
      // Now clip and draw the image
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(size - radius, 0);
      ctx.quadraticCurveTo(size, 0, size, radius);
      ctx.lineTo(size, size - radius);
      ctx.quadraticCurveTo(size, size, size - radius, size);
      ctx.lineTo(radius, size);
      ctx.quadraticCurveTo(0, size, 0, size - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();
      ctx.clip();
      // Draw image centered/cropped to square, bias top
      const sx = (img.width - size) / 2;
      const sy = 0;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Fetch a profile photo as base64 data URL for PDF embedding */
async function fetchPhotoAsBase64(s3Key: string): Promise<string | null> {
  try {
    const pdfClient = generateClient();
    const result = await pdfClient.graphql({
      query: `query($s3Key: String!) { getProfilePhotoDownloadUrl(s3Key: $s3Key) }`,
      variables: { s3Key },
      authMode: 'userPool',
    }) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;
    const url = result.data?.getProfilePhotoDownloadUrl;
    if (!url) return null;
    const response = await fetch(url);
    const blob = await response.blob();
    const rawBase64 = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
    if (!rawBase64) return null;
    // Apply rounded corners
    return await applyRoundedCorners(rawBase64, 30);
  } catch {
    return null;
  }
}

/** Register custom fonts with a jsPDF instance */
function registerFonts(doc: jsPDF) {
  doc.addFileToVFS('CormorantGaramond-Regular.ttf', cormorantRegular);
  doc.addFont('CormorantGaramond-Regular.ttf', 'Cormorant', 'normal');
  doc.addFileToVFS('CormorantGaramond-Bold.ttf', cormorantBold);
  doc.addFont('CormorantGaramond-Bold.ttf', 'Cormorant', 'bold');
  doc.addFileToVFS('CormorantGaramond-Italic.ttf', cormorantItalic);
  doc.addFont('CormorantGaramond-Italic.ttf', 'Cormorant', 'italic');
  // Also register italic as its own family for reliable access
  doc.addFont('CormorantGaramond-Italic.ttf', 'CormorantItalic', 'normal');
  doc.addFileToVFS('Montserrat-Regular.ttf', montserratRegular);
  doc.addFont('Montserrat-Regular.ttf', 'Montserrat', 'normal');
  doc.addFileToVFS('Montserrat-Bold.ttf', montserratBold);
  doc.addFont('Montserrat-Bold.ttf', 'Montserrat', 'bold');
}

// Pre-render crown SVG to PNG for PDF embedding
let crownPngCache: string | null = null;

function getCrownPng(): Promise<string> {
  if (crownPngCache) return Promise.resolve(crownPngCache);
  return new Promise((resolve) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 24 24" fill="none" stroke="#9c7c54" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.12"><path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"/><path d="M5 21h14"/></svg>`;
    const img = new Image();
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, 400, 400);
      }
      crownPngCache = canvas.toDataURL('image/png');
      URL.revokeObjectURL(url);
      resolve(crownPngCache);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(''); };
    img.src = url;
  });
}

function addCrownImg(doc: jsPDF, png: string, x: number, y: number, size: number) {
  if (png) { try { doc.addImage(png, 'PNG', x, y, size, size); } catch {} }
}

/** Draw header bar (no crown — crown is now at bottom-right of page) */
function drawHeader(doc: jsPDF, pw: number, headerH: number, _png: string) {
  doc.setFillColor(...G950);
  doc.rect(0, 0, pw, headerH, 'F');
}

/** Draw large rotated crown at bottom-right of page, hanging off the edge */
function drawBottomCrown(doc: jsPDF, png: string) {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const crownSize = 120; // large
  // Position so 1/4 hangs off the right edge and 1/4 hangs off the bottom (75% visible)
  const x = pw - crownSize * 0.75;
  const y = ph - crownSize * 0.75;
  addCrownImg(doc, png, x, y, crownSize);
}

/**
 * Draw a single profile page matching the gold-standard sidebar export template.
 * Used by both exportProfilePdf (single) and exportListPdf (bulk) for consistency.
 */
function drawProfilePage(
  doc: jsPDF,
  data: ProfileExportData,
  crownPng: string,
  photoBase64: string | null,
  sections: ExportSections,
) {
  const pw = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header bar
  drawHeader(doc, pw, 42, crownPng);

  // Header text
  doc.setTextColor(...G500);
  doc.setFontSize(36);
  doc.setFont('CormorantItalic', 'normal');
  doc.text('Idaho Pageants', 20, 22);
  doc.setFontSize(8);
  doc.setFont('Montserrat', 'normal');
  doc.setTextColor(...G400);
  doc.text('CONTESTANT PROFILE', 20, 31);
  doc.setFontSize(7);
  doc.setTextColor(...G300);
  doc.text(`Generated ${new Date().toLocaleDateString()}`, 20, 38);

  y = 56;

  // Avatar (right-aligned) — photo or initials fallback
  const avatarX = pw - 42;
  const avatarY = y - 4;
  const avatarSize = 30;
  if (photoBase64) {
    try {
      const format = photoBase64.includes('image/png') ? 'PNG' : 'JPEG';
      doc.addImage(photoBase64, format, avatarX, avatarY, avatarSize, avatarSize);
    } catch {
      drawInitialsAvatar(doc, data.fullName, avatarX, avatarY, avatarSize);
    }
  } else {
    drawInitialsAvatar(doc, data.fullName, avatarX, avatarY, avatarSize);
  }

  // Name
  doc.setTextColor(...G950);
  doc.setFontSize(20);
  doc.setFont('Cormorant', 'bold');
  doc.text(data.fullName, 20, y);
  y += 8;

  // Category line
  const cat = data.applicantType === 'MRS' ? 'Mrs.' : 'Miss';
  doc.setFontSize(8);
  doc.setFont('Montserrat', 'normal');
  doc.setTextColor(...G400);
  doc.text(`${cat} Division  ·  2026 Competition Year`, 20, y);
  y += 6;

  // Title
  const title = data.title || `${cat} Idaho America`;
  doc.setFontSize(11);
  doc.setFont('CormorantItalic', 'normal');
  doc.setTextColor(...G600);
  doc.text(title, 20, y);
  y += 7;

  y += 8;

  // Helpers
  const colW = (pw - 50) / 2;
  const c1 = 20, c2 = c1 + colW + 10;

  const hdr = (t: string) => {
    if (y > 258) { doc.addPage(); y = 20; }
    y += 3;
    doc.setFontSize(14); doc.setFont('Cormorant', 'bold'); doc.setTextColor(...G950);
    doc.text(t, 20, y);
    y += 3;
    doc.setDrawColor(...G500); doc.setLineWidth(0.25);
    doc.line(20, y, pw - 20, y);
    y += 7;
  };

  const pair = (l1: string, v1: string | undefined, l2: string, v2: string | undefined) => {
    if (!v1 && !v2) return;
    if (y > 268) { doc.addPage(); y = 20; }
    const sy = y;
    if (v1) {
      doc.setFontSize(6.5); doc.setFont('Montserrat', 'bold'); doc.setTextColor(...G400);
      doc.text(l1.toUpperCase(), c1, y);
      doc.setFontSize(8.5); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G950);
      doc.text(doc.splitTextToSize(v1, colW), c1, y + 4.5);
    }
    if (v2) {
      doc.setFontSize(6.5); doc.setFont('Montserrat', 'bold'); doc.setTextColor(...G400);
      doc.text(l2.toUpperCase(), c2, sy);
      doc.setFontSize(8.5); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G950);
      doc.text(doc.splitTextToSize(v2, colW), c2, sy + 4.5);
    }
    y += 13;
  };

  const full = (l: string, v: string | undefined) => {
    if (!v) return;
    if (y > 268) { doc.addPage(); y = 20; }
    doc.setFontSize(6.5); doc.setFont('Montserrat', 'bold'); doc.setTextColor(...G400);
    doc.text(l.toUpperCase(), 20, y);
    y += 4.5;
    doc.setFontSize(8.5); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G950);
    const lines = doc.splitTextToSize(v, pw - 40);
    doc.text(lines, 20, y);
    y += lines.length * 4.5 + 4;
  };

  // Contact Information
  if (sections.contactInfo) {
    hdr('Contact Information');
    pair('Email', data.email, 'Phone', data.phoneNumber);
    const addr = (() => {
      const street = data.address || '';
      const cityState = [data.city, data.state].filter(Boolean).join(', ');
      const line2 = [cityState, data.zip].filter(Boolean).join(' ');
      return [street, line2].filter(Boolean).join('\n') || undefined;
    })();
    pair('Address', addr, 'Social Media', data.socialMedia);
  }

  // Personal Details / Basic Info
  if (sections.basicInfo) {
    hdr('Personal Details');
    let age: string | undefined;
    if (data.dateOfBirth) {
      const b = new Date(data.dateOfBirth), t = new Date();
      let a = t.getFullYear() - b.getFullYear();
      if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
      if (a > 0) age = `${a}`;
    }
    pair('Date of Birth', data.dateOfBirth, 'Age', age);
    pair('Eye Color', data.eyeColor, 'Hair Color', data.hairColor);
    pair('US Citizen', data.usCitizen ? 'Yes' : 'No', '', undefined);
    pair('Occupation', data.occupation, 'Employer', data.employer);
    // Marital status for Miss contestants only
    if (data.applicantType === 'MISS' && data.maritalStatus) {
      const formattedStatus = data.maritalStatus === 'SINGLE' ? 'Single' :
        data.maritalStatus === 'DIVORCED' ? 'Divorced' :
        data.maritalStatus === 'WIDOWED' ? 'Widowed' : data.maritalStatus;
      pair('Marital Status', formattedStatus, '', undefined);
    }
  }

  // Additional Details / Background
  if (sections.additionalDetails) {
    hdr('Background');
    full('Community Involvement', data.communityInvolvement);
    full('Hobbies', data.hobbies);
    full('Talents', data.talents);
  }

  // Marriage Information
  if (sections.marriageInfo && data.applicantType === 'MRS' && (data.spouseName || data.marriageDate)) {
    hdr('Marriage Information');
    pair('Spouse Name', data.spouseName, 'Marriage Date', data.marriageDate);
  }

  // Stage Form
  if (sections.stageForm) {
    hdr('Stage Form');
    doc.setFontSize(8); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G400);
    doc.text('Data not yet available', 20, y); y += 8;
  }

  // Interview Info Sheet
  if (sections.interviewInfo) {
    hdr('Interview Info Sheet');
    doc.setFontSize(8); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G400);
    doc.text('Data not yet available', 20, y); y += 8;
  }

  // Placement Questions
  if (sections.placementQuestions) {
    hdr('Placement Questions');
    doc.setFontSize(8); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G400);
    doc.text('Data not yet available', 20, y); y += 8;
  }
}

/** Draw initials avatar fallback */
function drawInitialsAvatar(doc: jsPDF, fullName: string, x: number, y: number, size: number) {
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  doc.setFillColor(...G400);
  doc.roundedRect(x, y, size, size, 4, 4, 'F');
  doc.setTextColor(...G950);
  doc.setFontSize(14);
  doc.setFont('CormorantItalic', 'normal');
  doc.text(initials, x + size / 2, y + size / 2 + 1, { align: 'center' });
}

interface ProfileExportData {
  fullName: string;
  email: string;
  applicantType: string;
  profilePhotoS3Key?: string;
  title?: string;
  dateOfBirth?: string;
  eyeColor?: string;
  hairColor?: string;
  occupation?: string;
  employer?: string;
  socialMedia?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  usCitizen?: boolean;
  communityInvolvement?: string;
  hobbies?: string;
  talents?: string;
  spouseName?: string;
  marriageDate?: string;
  maritalStatus?: string;
  // Eligibility affidavit — signed status captured for admin records
  eligibilityAffidavitSigned?: boolean;
  eligibilityAffidavitSignedAt?: string;
  eligibilityAffidavitSignedName?: string;
  eligibilityAffidavitVersion?: string;
}



export async function exportProfilePdf(data: ProfileExportData) {
  const crownPng = await getCrownPng();
  // Fetch profile photo if available
  let photoBase64: string | null = null;
  if (data.profilePhotoS3Key) {
    photoBase64 = await fetchPhotoAsBase64(data.profilePhotoS3Key);
  }
  const doc = new jsPDF();
  registerFonts(doc);

  // All sections enabled for single-profile export
  const allSections: ExportSections = {
    basicInfo: true, contactInfo: true, additionalDetails: true,
    marriageInfo: true, stageForm: false, interviewInfo: false, placementQuestions: false,
  };

  drawProfilePage(doc, data, crownPng, photoBase64, allSections);

  // Footer
  const pw = doc.internal.pageSize.getWidth();
  const pc = doc.getNumberOfPages();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    drawBottomCrown(doc, crownPng);
    doc.setFontSize(6.5); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G300);
    doc.text(`Idaho Pageants  ·  Confidential  ·  Page ${i} of ${pc}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
  }

  doc.save(`${data.fullName.replace(/[^a-zA-Z0-9]/g, '_')}_Profile.pdf`);
}

export function buildExportData(app: { fullName: string; email: string; profilePhotoS3Key?: string }, profile: any): ProfileExportData {
  const p = profile?.phase1Data;
  let bg: any = {};
  if (p?.generalBackground) { try { bg = JSON.parse(p.generalBackground); } catch {} }
  return {
    fullName: app.fullName, email: app.email, applicantType: profile?.applicantType || '',
    profilePhotoS3Key: app.profilePhotoS3Key,
    title: profile?.approvedTitle || profile?.requestedTitle,
    dateOfBirth: p?.dateOfBirth, eyeColor: p?.eyeColor, hairColor: p?.hairColor,
    occupation: p?.occupation, employer: bg.nameOfEmployer, socialMedia: bg.socialMediaHandle,
    phoneNumber: bg.phoneNumber, address: bg.addressStreet, city: bg.addressCity,
    state: bg.addressState, zip: bg.addressZip, usCitizen: p?.usCitizen,
    communityInvolvement: p?.currentCommunityInvolvement, hobbies: p?.hobbies, talents: p?.talents,
    spouseName: p?.spouseName, marriageDate: p?.marriageDate,
    maritalStatus: p?.maritalStatus,
    eligibilityAffidavitSigned: p?.eligibilityAffidavitSigned,
    eligibilityAffidavitSignedAt: p?.eligibilityAffidavitSignedAt,
    eligibilityAffidavitSignedName: p?.eligibilityAffidavitSignedName,
    eligibilityAffidavitVersion: p?.eligibilityAffidavitVersion,
  };
}

export interface ExportSections {
  basicInfo: boolean; contactInfo: boolean; additionalDetails: boolean;
  marriageInfo: boolean; stageForm: boolean; interviewInfo: boolean; placementQuestions: boolean;
}

export const DEFAULT_EXPORT_SECTIONS: ExportSections = {
  basicInfo: true, contactInfo: true, additionalDetails: true, marriageInfo: true,
  stageForm: false, interviewInfo: false, placementQuestions: false,
};

export async function exportListPdf(profiles: ProfileExportData[], sections: ExportSections) {
  const crownPng = await getCrownPng();
  // Prefetch all profile photos in parallel
  const photoMap = new Map<string, string>();
  const photoPromises = profiles
    .filter(p => p.profilePhotoS3Key)
    .map(async (p) => {
      const base64 = await fetchPhotoAsBase64(p.profilePhotoS3Key!);
      if (base64) photoMap.set(p.profilePhotoS3Key!, base64);
    });
  await Promise.all(photoPromises);

  const doc = new jsPDF();
  registerFonts(doc);
  const pw = doc.internal.pageSize.getWidth();

  profiles.forEach((data, idx) => {
    if (idx > 0) doc.addPage();
    const photoBase64 = data.profilePhotoS3Key ? photoMap.get(data.profilePhotoS3Key) ?? null : null;
    drawProfilePage(doc, data, crownPng, photoBase64, sections);
  });

  const pc = doc.getNumberOfPages();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    drawBottomCrown(doc, crownPng);
    doc.setFontSize(6.5); doc.setFont('Montserrat', 'normal'); doc.setTextColor(...G300);
    doc.text(`Idaho Pageants  ·  Contestant Roster  ·  Page ${i} of ${pc}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
  }
  doc.save(`Idaho_Pageants_Contestants_${new Date().toISOString().split('T')[0]}.pdf`);
}
